import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiFetch from "../../utlis/apiFetch";
import { setGlobalCountries } from "./countriesSlice";

export const fetchCountries = createAsyncThunk(
  "filters/fetchCountries",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return [];

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/country`,
        {
          headers: {
            authorization: `bearer ${token}`,
          },
        }
      );

      const data = response._data ?? await response.json();
      const fetchedCountries = data?.body?.countries || [];

      return fetchedCountries.map((ct) => ({
        _id: ct._id,
        name: ct.name,
        flag: ct.countryCode
          ? `https://flagcdn.com/w80/${ct.countryCode.toLowerCase()}.png`
          : "",
        code: ct.countryCode,
        dial_code: ct.dialCode,
      }));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchVisaTypes = createAsyncThunk(
  "filters/fetchVisaTypes",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return [];

      const state = getState();
      const countries = state.filters.countries;

      let queryParams = "";
      const savedCountries = localStorage.getItem("globalCountries");
      let needsCountries = false;

      if (savedCountries) {
        try {
          const parsedCountries = JSON.parse(savedCountries);
          if (Array.isArray(parsedCountries) && parsedCountries.length > 0) {
            needsCountries = true;
            const selectedIds = [];
            parsedCountries.forEach((name) => {
              const country = countries.find((c) => c.name === name);
              if (country) selectedIds.push(country._id);
            });

            if (selectedIds.length > 0) {
              queryParams = `?${selectedIds
                .map((id) => `countryId=${id}`)
                .join("&")}`;
            }
          }
        } catch (error) {
          console.error("Error parsing globalCountries:", error);
        }
      }

      // If we need countries (because of LS selection) but don't have them in state yet, we can't filter correctly.
      // The original hook returns early in this case.
      // Here, we can perhaps return an empty array or handle it.
      // But if we return empty, we overwrite the state.
      // Ideally, the caller ensures countries are loaded.
      if (needsCountries && countries.length === 0) {
        // We technically shouldn't fetch if valid countries are missing but required.
        // Returning [] might be misleading if it's just 'not ready'.
        // But let's return [] to match previous behavior (which didn't setVisaTypes, leaving it as [] or previous)
        return [];
      }

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/visatype${queryParams}`,
        {
          method: "GET",
          headers: {
            authorization: `bearer ${token}`,
          },
        }
      );

      const data = response._data ?? await response.json();
      const allVisaTypes = data?.body?.visaTypes || [];

      // Remove duplicates based on title
      const uniqueVisaTypes = allVisaTypes.reduce((acc, current) => {
        const x = acc.find((item) => item.title === current.title);
        if (!x) {
          return acc.concat([current]);
        } else {
          return acc;
        }
      }, []);

      return [{ _id: "all", title: "All" }, ...uniqueVisaTypes];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const filterSlice = createSlice({
  name: "filters",
  initialState: {
    countries: [],
    visaTypes: [],
    countriesStatus: "idle", // idle | loading | succeeded | failed
    visaTypesStatus: "idle",
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Countries
      .addCase(fetchCountries.pending, (state) => {
        state.countriesStatus = "loading";
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countriesStatus = "succeeded";
        state.countries = action.payload;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.countriesStatus = "failed";
        state.error = action.payload;
      })
      // Visa Types
      .addCase(fetchVisaTypes.pending, (state) => {
        state.visaTypesStatus = "loading";
      })
      .addCase(fetchVisaTypes.fulfilled, (state, action) => {
        state.visaTypesStatus = "succeeded";
        state.visaTypes = action.payload;
      })
      .addCase(fetchVisaTypes.rejected, (state, action) => {
        state.visaTypesStatus = "failed";
        state.error = action.payload;
      })
      .addCase(setGlobalCountries, (state) => {
        state.visaTypesStatus = "idle";
        state.countriesStatus = "idle";
      });
  },
});

export default filterSlice.reducer;
